import { useEffect, useRef, useState } from 'react';

export interface CountdownTimerProps {
  expiresAt: Date;
  onExpired: () => void;
  variant?: 'large' | 'inline';
}

/**
 * `MM:SS`, or `H:MM:SS` once there is an hour or more left.
 *
 * The payment window is two hours, set by Bitcoin's block time. Without the
 * hour component a fresh session reads `120:00`, which is not a duration
 * anybody parses at a glance.
 */
function formatRemaining(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mmSs = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return h > 0 ? `${h}:${mmSs}` : mmSs;
}

export default function CountdownTimer({ expiresAt, onExpired, variant = 'large' }: CountdownTimerProps) {
  const fired = useRef(false);
  const [remainingSec, setRemainingSec] = useState(() => {
    const diff = Math.floor((expiresAt.getTime() - Date.now()) / 1000);
    return Math.max(0, diff);
  });

  useEffect(() => {
    fired.current = false;
    const tick = () => {
      const diff = Math.floor((expiresAt.getTime() - Date.now()) / 1000);
      const next = Math.max(0, diff);
      setRemainingSec(next);
      if (next === 0 && !fired.current) {
        fired.current = true;
        onExpired();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onExpired]);

  const urgent = remainingSec > 0 && remainingSec < 120;

  if (variant === 'inline') {
    return (
      <span style={{ color: urgent ? 'var(--ps-error)' : 'inherit', fontVariantNumeric: 'tabular-nums' }}>
        {formatRemaining(remainingSec)}
      </span>
    );
  }

  return <p className={`ps-time${urgent ? ' urgent' : ''}`}>{formatRemaining(remainingSec)}</p>;
}
